/**
 * `garmentOrders.*` wire contract (ADR-0039, `garment.styles` module).
 *
 * The commercial intake that precedes the Style File: buyer, PO details,
 * delivery — and one or more GARMENTS (ADR-0043), each with its own
 * colour × size × qty grid (ADR-0041) and spec.
 * Quantities are plain integer pieces. The procedures sit behind
 * `entitledProcedureFor("garment.styles")` on the server.
 *
 * Garment-type keys reuse the `styles` contract enum (same eight templates).
 */
import { z } from "zod";
import { uuid, paged, isoDate } from "../_common.js";
import { garmentType } from "./styles.js";

export const garmentOrderStatus = z.enum(["open", "accepted", "linked", "closed", "cancelled"]);

const qtyInt = z.number().int().min(0).max(10_000_000);

/** One colour × size cell. The grid is sparse — unordered cells are absent. */
export const orderGridCellInput = z.object({
  colour: z.string().trim().min(1).max(40),
  colourHex: z.string().trim().max(7).nullable().optional(),
  size: z.string().trim().min(1).max(16),
  qty: qtyInt,
});

/** One garment on the order, with its own grid. */
export const orderItemInput = z.object({
  garmentType,
  name: z.string().trim().min(1).max(160),
  spec: z.string().trim().max(2000).nullable().optional(),
  lines: z.array(orderGridCellInput).min(1).max(240),
});

export const createInput = z.object({
  buyerId: uuid,
  description: z.string().trim().max(240).nullable().optional(),
  buyerRef: z.string().trim().max(40).nullable().optional(),
  /** Buyer's formal PO — optional; acceptance never waits on paperwork. */
  poNumber: z.string().trim().max(40).nullable().optional(),
  poDate: isoDate.nullable().optional(),
  deliveryDate: isoDate.nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  items: z.array(orderItemInput).min(1).max(40),
});
export const createOutput = z.object({ id: uuid, orderNo: z.string() });

export const getInput = z.object({ id: uuid });

const orderGridLine = z.object({
  id: uuid,
  position: z.number().int().nonnegative(),
  colour: z.string(),
  colourHex: z.string().nullable(),
  size: z.string(),
  qty: z.number().int().nonnegative(),
});

const orderItem = z.object({
  id: uuid,
  position: z.number().int().nonnegative(),
  garmentType: z.string(),
  name: z.string(),
  spec: z.string().nullable(),
  styleId: uuid.nullable(),
  styleRef: z.string().nullable(),
  styleName: z.string().nullable(),
  lines: z.array(orderGridLine),
  totalQty: z.number().int().nonnegative(),
});

export const orderDetail = z.object({
  id: uuid,
  orderNo: z.string(),
  description: z.string().nullable(),
  status: garmentOrderStatus,
  buyerId: uuid,
  buyerName: z.string(),
  buyerCity: z.string().nullable(),
  buyerState: z.string().nullable(),
  buyerRef: z.string().nullable(),
  poNumber: z.string().nullable(),
  poDate: z.string().nullable(),
  deliveryDate: isoDate.nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  createdByActorId: z.string(),
  createdByActorType: z.string(),
  items: z.array(orderItem),
  totalQty: z.number().int().nonnegative(),
});
export const getOutput = orderDetail;

export const updateInput = z.object({
  id: uuid,
  buyerId: uuid,
  description: z.string().trim().max(240).nullable().optional(),
  buyerRef: z.string().trim().max(40).nullable().optional(),
  poNumber: z.string().trim().max(40).nullable().optional(),
  poDate: isoDate.nullable().optional(),
  deliveryDate: isoDate.nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  items: z.array(orderItemInput).min(1).max(40),
});
export const updateOutput = z.object({ id: uuid });

/** Engineering links ONE garment at a time (ADR-0043). */
export const linkStyleInput = z.object({ itemId: uuid, styleId: uuid });
export const linkStyleOutput = z.object({
  itemId: uuid,
  orderId: uuid,
  styleId: uuid,
  status: garmentOrderStatus,
});

export const transitionStatusInput = z.object({ id: uuid, status: garmentOrderStatus });
export const transitionStatusOutput = z.object({ id: uuid, status: garmentOrderStatus });

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  status: garmentOrderStatus.optional(),
  buyerId: uuid.optional(),
  search: z.string().trim().min(1).max(80).optional(),
});

export const listOutput = paged(
  z.object({
    id: uuid,
    orderNo: z.string(),
    description: z.string().nullable(),
    status: garmentOrderStatus,
    buyerId: uuid,
    buyerName: z.string(),
    buyerRef: z.string().nullable(),
    deliveryDate: isoDate.nullable(),
    totalQty: z.number().int().nonnegative(),
    sizeCount: z.number().int().nonnegative(),
    colourCount: z.number().int().nonnegative(),
    itemCount: z.number().int().nonnegative(),
    firstItemName: z.string().nullable(),
    linkedItemCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  }),
).extend({
  counts: z.object({
    all: z.number().int().nonnegative(),
    open: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    linked: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
  }),
  kpis: z.object({
    activeCount: z.number().int().nonnegative(),
    activePieces: z.number().int().nonnegative(),
    unlinkedCount: z.number().int().nonnegative(),
    deliveryThisWeekCount: z.number().int().nonnegative(),
  }),
});
